import { useEffect, useMemo, useRef } from 'react'
import FirebaseService from '../../helpers/FireBaseService'
import { NOTIFY_ACTION } from '../../context/action'
import { TAction, TAppProps, TAppState } from '../../context/types'
import firebase from 'firebase'

const PREFIX_NEARLY_EXPIRED_JOBS = 'E-'

type TUseFirebase = {
    state: TAppState
    props: TAppProps
    dispatch: React.Dispatch<TAction>
}

const useFirebase = (params: TUseFirebase) => {
    const { state, props, dispatch } = params
    const { notificationCount, notificationBellCount } = state

    const firebaseConnected = useRef<boolean>(false)
    const isValueChanged = useRef<boolean>(false)

    const firebaseService = useMemo(() => {
        return new FirebaseService(props.config?.firebaseConfig)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isAuthenticated = () => {
        const { currentUser } = firebaseService.getAuth()
        return Boolean(currentUser)
    }
    /**
     * Get notifications count of user
     * @returns Promise<firebase.database.Reference>
     */
    const getUserNotificationCountRef = async (): Promise<firebase.database.Reference | null> => {
        const userId = await firebaseService.getUserId()
        if (!userId) {
            return null
        }
        return firebaseService.getDatabase().ref(`my-notification-users/${userId}/noOfNotifications`)
    }

    const getUserBellNotificationCountRef = async (): Promise<firebase.database.Reference | null> => {
        const userId = await firebaseService.getUserId()
        if (!userId) {
            return null
        }
        return firebaseService.getDatabase().ref(`my-notification-users/${userId}/noOfBellNotifications`)
    }

    const getUserNotificationsRef = async (): Promise<firebase.database.Query | null> => {
        const userId = await firebaseService.getUserId()
        if (!userId) {
            return null
        }

        return firebaseService
            .getDatabase()
            .ref(`my-notification-users/${userId}/notifications`)
            .orderByChild('sentDate')
            .limitToLast(20)
    }

    const getOneUserNotificationRef = async (index: string) => {
        const userId = await firebaseService.getUserId()

        return firebaseService.getDatabase().ref(`my-notification-users/${userId}/notifications/${index}`)
    }
    // End Ref
    /// ///////////////////////////////////////////////////////////
    const _resetNumOfNotifications = async () => {
        const userRef = await getUserNotificationCountRef()
        if (!userRef) {
            return 0
        }
        return userRef.transaction((noOfNotifications) => {
            if (noOfNotifications) {
                noOfNotifications = 0
            }
            return noOfNotifications
        })
    }

    // End Reset
    /// ///////////////////////////////////////////////////////////
    const _removeExpiredJob = async (jobId: number) => {
        if (jobId <= 0 || !isAuthenticated()) {
            return
        }
        const userId = await firebaseService.getUserId()
        const jobNotificationRef = firebaseService
            .getDatabase()
            .ref(`my-notification-users/${userId}/notifications/${PREFIX_NEARLY_EXPIRED_JOBS}${jobId}`)
        jobNotificationRef.remove()
    }

    const _onValueChange = async (snapshot: firebase.database.DataSnapshot) => {
        const snapshotValue = snapshot.val()
        if (snapshotValue === null) {
            dispatch({
                type: NOTIFY_ACTION.SET_NOTIFICATIONS,
                payload: []
            })
            return
        }
        const keys = Object.keys(snapshotValue)
        const values = Object.values(snapshotValue)

        const newNotifications = values.map((item: any, index: number) => {
            let { jobId, expiredDate } = item
            expiredDate = expiredDate * 1000 || Date.now()

            const remainDays = (expiredDate - Date.now()) / 86400000
            if (remainDays <= 0) {
                _removeExpiredJob(jobId)
            }
            const notifyItem = {
                notifyKey: keys[index],
                ...item
            }
            if (item.statusId) {
                notifyItem.statusId = Number(item.statusId)
            }
            return notifyItem
        })

        dispatch({
            type: NOTIFY_ACTION.SET_NOTIFICATIONS,
            payload: newNotifications.sort((a, b) => b.sentDate - a.sentDate)
        })
        isValueChanged.current = true
    }

    const _getNotifications = async () => {
        if (!isAuthenticated()) {
            return
        }
        const userRef = await getUserNotificationsRef()
        if (!userRef) {
            return
        }
        userRef.off('child_added')
        userRef.on('child_added', async (snapshot) => {
            // This function should run when received new notification only
            // isValueChanged Ref was used to prevent duplicate notification at initial time
            if (!isValueChanged.current) {
                return
            }
            const snapshotKey = snapshot.key ?? ''
            const snapshotValue = snapshot.val()
            // Do not push any null to array of notifications
            if (snapshotKey === null || snapshotKey === '' || snapshotValue === null) {
                return
            }

            // Push to array notification
            dispatch({
                type: NOTIFY_ACTION.PUSH_TO_NOTIFICATIONS,
                payload: { ...snapshotValue, notifyKey: snapshotKey }
            })
        })
        userRef.once('value', _onValueChange)
    }

    /**
     * Get Notification, trigger when user want to view list of notifications
     */
    const getNotification = async () => {
        dispatch({
            type: NOTIFY_ACTION.SET_LOADING,
            payload: true
        })
        firebaseService.getAuth().onAuthStateChanged(async (user: firebase.User | null) => {
            if (!user) {
                return
            }
            // Reset number of notifications to zero to display there're nothing new notificaions
            _resetNumOfNotifications().then((result) =>
                dispatch({
                    type: NOTIFY_ACTION.SET_NOTIFY_COUNT,
                    payload: {
                        type: 'notifyBell',
                        value: result.snapshot.val()
                    }
                })
            )

            // [Firebase] Read a list of Expiring Jobs and Who Viewed My Profile notifications at
            // my-notification-users/<JobseekerId>/notifications
            _getNotifications()
        })
    }
    /// ///////////////////////////////////////////////////////////
    const setReadNotificationItem = async (notifyKey: string) => {
        if (!isAuthenticated()) {
            return
        }
        /**
         * [Firebase] Mark notification as read
         */
        const userRef = await getOneUserNotificationRef(notifyKey)
        await userRef.update({ isRead: 1 })
        // Update notifications list state
        dispatch({
            type: NOTIFY_ACTION.SET_NOTIFY_READ,
            payload: notifyKey
        })
    }
    /**
     * Check if user is logged out then reset notification count
     *
     */
    const resetNotifyWhenLoggedOut = async () => {
        if (props.user?.userId) {
            return
        }
        await firebaseService.signOut()
        if (notificationCount > 0) {
            // Reset Notification count
            dispatch({
                type: NOTIFY_ACTION.SET_NOTIFY_COUNT,
                payload: {
                    type: 'notify',
                    value: 0
                }
            })
        }
        if (notificationBellCount > 0) {
            dispatch({
                type: NOTIFY_ACTION.SET_NOTIFY_COUNT,
                payload: {
                    type: 'notifyBell',
                    value: 0
                }
            })
        }
    }

    const authenticateOnce = async () => {
        if (firebaseConnected.current) {
            return
        }
        // We don't listing productNews so
        // do not call authenticate firebase when user is not logged in
        if (!props.user?.userId) {
            return
        }
        await firebaseService.authenticatedConnect()
        firebaseConnected.current = true
    }

    const authConnect = async () => {
        await firebaseService.authenticatedConnect()
    }

    const onNotificationCountChanged = async () => {
        const userRef = await getUserNotificationCountRef()
        if (!userRef) {
            return
        }
        userRef.on('value', (snapshot) =>
            dispatch({
                type: NOTIFY_ACTION.SET_NOTIFY_COUNT,
                payload: {
                    type: 'notify',
                    value: snapshot.val()
                }
            })
        )
    }

    const onBellNotificationCountChanged = async () => {
        const userRef = await getUserBellNotificationCountRef()
        if (!userRef) {
            return
        }
        userRef.on('value', (snapshot) =>
            dispatch({
                type: NOTIFY_ACTION.SET_NOTIFY_COUNT,
                payload: {
                    type: 'notifyBell',
                    value: snapshot.val()
                }
            })
        )
    }

    const doFetchNotification = async () => {
        resetNotifyWhenLoggedOut()
        // Make a global connection promise to reduce mutiple connections at same time
        await authenticateOnce()

        // Watch and update number of notifications state from the user
        onNotificationCountChanged()
        onBellNotificationCountChanged()
    }

    useEffect(() => {
        doFetchNotification()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Additional missing
     * - Add a watch for user change and call 'authenticateOnce' again
     */
    return {
        getNotification,
        setReadNotificationItem,
        doAuthenticate: authConnect
    }
}

export default useFirebase
